import moment from "moment";

export interface ScopeCertificateDetails {
  scopeCertificateNo: string; // Numeric field
  brandName: string[]; // Multi-select dropdown
  gotsSCNo: string; // Optional text field
  scLegacyNo: string[]; // Optional text field
  scCBAuditorId: string[]; // Dropdown
  scStatus: string; // Dropdown
  scStatusDescription: string; // Description for scStatus dropdown
  scSuspensionReason: string; // Dropdown
  scSuspensionReasonDescription: string; // Description for scSuspensionReason dropdown
  scCBLicensingCode: string; // Text field
  scVersionNo: string; // Numeric field
  scProductLimitations: string; // New field from API
  scCreationTimestamp: string; // New field from API
  scCbLicensingCodeDescription: string; // New field from API
  ccsFlag: string; // Does CCS Apply field (radio - no description needed)
  // Removed: scCertifiedOrganizationApprovedClaims - moved back to StandardDetails
}

export interface StandardDetails {
  scStandard: string; // Text field
  scStandardDescription?: string;
  scStandardVersion: string; // Text field
  scNumberOfFarms: number; // Numeric field - API field name (changed from string to number)
  scStandardAddedDate: string; // Date field - API field name
  scStandardRemovedDate: string; // Date field - API field name
  scStandardWithdrawalReason: string[]; // Multi-select dropdown field - API field name
  scStandardWithdrawalReasonDescription: string; // Description for scStandardWithdrawalReason dropdown
  scCertifiedOrganizationApprovedClaims: string; // Required field for API - moved back to StandardDetails
  // Removed: numberOfAuditDays, certifiedOrganizationApprovedClaimsDescription
}

export interface ScopeCertificateDates {
  scCreatedDate: string; // Date in 'YYYY-MM-DD' format (required)
  scLastUpdatedDate: string; // Date in 'YYYY-MM-DD' format (required) - Updated to match API field name
  scExtendedUntilDate: string; // Date in 'YYYY-MM-DD' format (optional) - Updated to match API field name
  scLastAuditStartDate: string; // Date in 'YYYY-MM-DD' format (optional) - Updated to match API field name
  scValidFromDate: string; // Date in 'YYYY-MM-DD' format (required) - Updated to match API field name
  scValidUntilDate: string; // Date in 'YYYY-MM-DD' format (required) - Updated to match API field name
  scWithdrawalDate: string; // Date in 'YYYY-MM-DD' format (optional)
  scLastAuditDate: string; // Date in 'YYYY-MM-DD' format (optional)
  scStateOrProvince: string; // Dropdown (required)
  scStateOrProvinceDescription: string; // Description for scStateOrProvince dropdown
  scPlaceOfIssue: string; // StateName (CountryCode-StateCode) for API (required)
  scCbAccreditationBody: string; // Dropdown (required) - API field name
  scCbAccreditationBodyDescription: string; // Description for scCbAccreditationBody dropdown
  scCbScopeStipulations: string; // Text field (optional) - API field name
}

export interface AccreditationDetails {
  scScopeStipulations: string; // Optional text field
  accreditationBody: string; // Dropdown
  accreditationBodyDescription: string; // Description for accreditationBody dropdown
  accreditationNo: string; // Optional text field
  inspectionBody: string; // Optional text field
}

export interface NonMandatoryCriterionDetails {
  scNonMandatoryCriterionStandard: string; // Dropdown field (required)
  scNonMandatoryCriterionStandardDescription: string; // Description for scNonMandatoryCriterionStandard dropdown
  scNonMandatoryCriterionStandardVersion: string; // Text field (required)
  scNonMandatoryCriterionNo: string; // Text field (required)
  scNonMandatoryCriterionStatus: string; // Dropdown field (required)
  scNonMandatoryCriterionStatusDescription: string; // Description for scNonMandatoryCriterionStatus dropdown
}

export interface ScopeCertificateForm {
  scopeCertificateDetails: ScopeCertificateDetails;
  standardDetails: StandardDetails[];
  scopeCertificateDates: ScopeCertificateDates;
  scNonMandatoryCriterion: NonMandatoryCriterionDetails[];
  // Removed: accreditationDetails
}

export function mapToScopeCertificateForm(response: any): ScopeCertificateForm {
  // Parse place of issue to extract state code
  const placeOfIssue = response.scPlaceOfIssue || "";
  // Extract state code from format like "Chlef (DZ-02)" -> "02"
  const stateMatch = placeOfIssue.match(/\([A-Z]{2}-([A-Z0-9]+)\)/);
  const state = stateMatch ? stateMatch[1] : "";
  
  return {
    scopeCertificateDetails: {
      scopeCertificateNo: response.scNo || "", // Converting scId to number
      brandName: response.brandName || [], // Wrapping brandName in an array
      gotsSCNo: response.gotsScNo || "", // Optional field
      scLegacyNo: response.scLegacyNo || [], // Optional field
      scCBAuditorId: response.scCbAuditorId || [], // Taking the first auditor ID
      scStatus: response.scStatus || "", // Dropdown value
      scStatusDescription: response.scStatusDescription || "", // Description for scStatus dropdown
      scSuspensionReason: response.scSuspensionReason || "", // Dropdown value
      scSuspensionReasonDescription: response.scSuspensionReasonDescription || "", // Description for scSuspensionReason dropdown
      scCBLicensingCode: response.scCbLicensingCode || "",
      scVersionNo: response.scVersionNo || "",
      scProductLimitations: response.scProductLimitations || "", // New field from API
      scCreationTimestamp: response.scCreationTimestamp || "", // New field from API
      scCbLicensingCodeDescription: response.scCbLicensingCodeDescription || "", // New field from API
      ccsFlag: response.ccsFlag || "", // Does CCS Apply field
      // Removed: scCertifiedOrganizationApprovedClaims - moved back to StandardDetails
    },
    standardDetails: response.scStandardDetails?.length > 0 ? response.scStandardDetails.map((item: any) => ({
      scStandard: item.scStandard,
      scStandardDescription: item.scStandardDescription,
      scStandardVersion: item.scStandardVersion,
      scNumberOfFarms: item.scNumberOfFarms || 0,  // API field name - ensure it's a number
      scStandardAddedDate: item.scStandardAddedDate,  // API field name
      scStandardRemovedDate: item.scStandardRemovedDate,  // API field name
      scStandardWithdrawalReason: Array.isArray(item.scStandardWithdrawalReason) 
        ? item.scStandardWithdrawalReason.map((reason: string) => {
            // Parse "code; description" format back to just the description for form display
            const parts = reason.split('; ');
            return parts.length > 1 ? parts[1] : reason;
          })
        : [],  // API field name - convert from ["code1; description1", "code2; description2"] to ["description1", "description2"]
      scStandardWithdrawalReasonDescription: item.scStandardWithdrawalReasonDescription || "", // Description for scStandardWithdrawalReason dropdown
      scCertifiedOrganizationApprovedClaims: item.scCertifiedOrganizationApprovedClaims || "", // Required field for API - moved back to StandardDetails
    })) : [],
    scopeCertificateDates: {
      scCreatedDate: response.scCreatedDate || "",
      scLastUpdatedDate: response.scLastUpdatedDate || "", // Updated to match API field name
      scExtendedUntilDate: response.scExtendedUntilDate || "", // Updated to match API field name
      scLastAuditStartDate: response.scLastAuditStartDate || "", // Updated to match API field name
      scValidFromDate: response.scValidFromDate || "", // Updated to match API field name
      scValidUntilDate: response.scValidUntilDate || "", // Updated to match API field name
      scWithdrawalDate: response.scWithdrawalDate || "",
      scLastAuditDate: response.scLastAuditDate || "",
      scStateOrProvince: state || "",
      scStateOrProvinceDescription: response.scStateOrProvinceDescription || "", // Description for scStateOrProvince dropdown
      scPlaceOfIssue: response.scPlaceOfIssue || "",
      scCbAccreditationBody: response.scCbAccreditationBody || "",
      scCbAccreditationBodyDescription: response.scCbAccreditationBodyDescription || "", // Description for scCbAccreditationBody dropdown
      scCbScopeStipulations: response.scCbScopeStipulations || "",
    },
    scNonMandatoryCriterion: response.scNonMandatoryCriterion?.length > 0 ? response.scNonMandatoryCriterion.map((item: any) => ({
      scNonMandatoryCriterionStandard: item.scNonMandatoryCriterionStandard || "",
      scNonMandatoryCriterionStandardDescription: item.scNonMandatoryCriterionStandardDescription || "", // Description for scNonMandatoryCriterionStandard dropdown
      scNonMandatoryCriterionStandardVersion: item.scNonMandatoryCriterionStandardVersion || "",
      scNonMandatoryCriterionNo: item.scNonMandatoryCriterionNo || "",
      scNonMandatoryCriterionStatus: item.scNonMandatoryCriterionStatus || "",
      scNonMandatoryCriterionStatusDescription: item.scNonMandatoryCriterionStatusDescription || "", // Description for scNonMandatoryCriterionStatus dropdown
    })) : [],
  };
}

export function scopeCertificateFormToMap(scopeCertificateForm: ScopeCertificateForm, SC_ID?: string | undefined, cbLicenseCode?: any): any {
  const { scopeCertificateDetails, standardDetails, scopeCertificateDates, scNonMandatoryCriterion } = scopeCertificateForm;
  
  // Use the formatted place of issue directly
  const placeOfIssue = scopeCertificateDates.scPlaceOfIssue || "";
  
  const statusOptions: any = [
    { label: "Valid", value: "V" }, // Represents "Valid"
    { label: "Expired", value: "E" }, // Represents "Expired"
    { label: "Suspended", value: "S" }, // Represents "Suspended"
    { label: "Withdrawn", value: "W" }, // Represents "Withdrawn"
  ]
  const scStandardOptions: any = [
    { label: "Organic Content Standard", value: "OCS" }, // Represents "Valid"
    { label: "Global Recycled Standard", value: "GRS" }, // Represents "Expired" 
    { label: "Recycled Claim Standard", value: "RCS" }, // Represents "Suspended"
    { label: "Responsible Down Standard", value: "RDS" }, // Represents "Withdrawn"
    { label: "Responsible Wool Standard", value: "RWS" }, // Represents "Withdrawn"
    { label: "Responsible Mohair Standard", value: "RMS" }, // Represents "Withdrawn"
    { label: "Responsible Alpaca Standard", value: "RAS" }, // Represents "Withdrawn"
    { label: "Content Claim Standard", value: "CCS" }, // Represents "Withdrawn"
  ]

  const standardWithdrawalReasonValues: any = [
    { label: "Critical non-conformity", value: "Critical non-conformity", code: "C" },
    { label: "Non-conformity", value: "Non-conformity", code: "N" },
    { label: "Recertification completed", value: "Recertification completed", code: "R" },
    { label: "Withdrawal of the certification body's accreditation", value: "Withdrawal of the certification body's accreditation", code: "W" },
    { label: "Administrative", value: "Administrative", code: "A" },
    { label: "Voluntary", value: "Voluntary", code: "V" },
    { label: "Error", value: "Error", code: "E" },
    { label: "Other", value: "Other", code: "O" },
  ]

  return {
    scId: SC_ID,
    scNo: scopeCertificateDetails.scopeCertificateNo,
    scVersionNo: scopeCertificateDetails.scVersionNo.toString(), // Convert number to string
    gotsScNo: scopeCertificateDetails.gotsSCNo,
    brandName: scopeCertificateDetails.brandName,
    scCbAuditorId: scopeCertificateDetails.scCBAuditorId,
    scLegacyNo: scopeCertificateDetails.scLegacyNo,
    scStatus: scopeCertificateDetails.scStatus,
    scStatusDescription: scopeCertificateDetails.scStatusDescription,
    scSuspensionReason: scopeCertificateDetails.scSuspensionReason,
    scSuspensionReasonDescription: scopeCertificateDetails.scSuspensionReasonDescription,
    ccsFlag: scopeCertificateDetails.ccsFlag,
    scCbLicensingCode: cbLicenseCode,
    scCreatedDate: scopeCertificateDates.scCreatedDate || "",
    scLastUpdatedDate: scopeCertificateDates.scLastUpdatedDate || "", // Updated to match API field name
    scExtendedUntilDate: scopeCertificateDates.scExtendedUntilDate || "", // Updated to match API field name
    scLastAuditStartDate: scopeCertificateDates.scLastAuditStartDate || "", // Updated to match API field name
    scValidFromDate: scopeCertificateDates.scValidFromDate || "", // Updated to match API field name
    scValidUntilDate: scopeCertificateDates.scValidUntilDate || "", // Updated to match API field name
    scWithdrawalDate: scopeCertificateDates.scWithdrawalDate || "",
    scLastAuditDate: scopeCertificateDates.scLastAuditDate || "",
    scPlaceOfIssue: placeOfIssue,
    scCbAccreditationBody: scopeCertificateDates.scCbAccreditationBody || "",
    scCbAccreditationBodyDescription: scopeCertificateDates.scCbAccreditationBodyDescription || "", // Description for scCbAccreditationBody dropdown
    scCbScopeStipulations: scopeCertificateDates.scCbScopeStipulations || "",
    scCreationTimestamp: scopeCertificateDetails.scCreationTimestamp || new Date().toISOString(), // Add missing field from API
    scProductLimitations: scopeCertificateDetails.scProductLimitations || "", // Add missing field from API
    // Removed: scCertifiedOrganizationApprovedClaims - moved back to StandardDetails
    scStandardDetails: standardDetails.length > 0 ? standardDetails.map((item: any) => ({
      scStandard: item.scStandard || "",
      scStandardVersion: item.scStandardVersion || "",
      scNumberOfFarms: item.scNumberOfFarms || 0,  // API field name - ensure it's a number
      scStandardAddedDate: item.scStandardAddedDate || "",  // API field name
      scStandardRemovedDate: item.scStandardRemovedDate || "",  // API field name
      scStandardWithdrawalReason: Array.isArray(item.scStandardWithdrawalReason) 
        ? item.scStandardWithdrawalReason.map((reason: string) => {
            // Find the option to get both code and description
            const option = standardWithdrawalReasonValues.find((opt: any) => opt.value === reason);
            return option ? `${option.code}; ${option.label}` : reason;
          })
        : [],  // API field name - format as ["code1; description1", "code2; description2"]
      scStandardWithdrawalReasonDescription: item.scStandardWithdrawalReasonDescription || "", // Description for scStandardWithdrawalReason dropdown
      scCertifiedOrganizationApprovedClaims: item.scCertifiedOrganizationApprovedClaims || "", // Required field for API - moved back to StandardDetails
    })) : [],
    scNonMandatoryCriterion: scNonMandatoryCriterion.length > 0 ? scNonMandatoryCriterion.map((item: any) => ({
      scNonMandatoryCriterionStandard: item.scNonMandatoryCriterionStandard || "",
      scNonMandatoryCriterionStandardDescription: item.scNonMandatoryCriterionStandardDescription || "", // Description for scNonMandatoryCriterionStandard dropdown
      scNonMandatoryCriterionStandardVersion: item.scNonMandatoryCriterionStandardVersion || "",
      scNonMandatoryCriterionNo: item.scNonMandatoryCriterionNo || "",
      scNonMandatoryCriterionStatus: item.scNonMandatoryCriterionStatus || "",
      scNonMandatoryCriterionStatusDescription: item.scNonMandatoryCriterionStatusDescription || "", // Description for scNonMandatoryCriterionStatus dropdown
    })) : [],
    scCbLicensingCodeDescription: scopeCertificateDetails.scCbLicensingCodeDescription || "", // Add missing field from API
  };
}
